// The lockup that names every UMaps tool: the circle mark, UMaps in bold, and
// what this particular page is.
//
//     [◎] UMaps Building Directories
//     [◎] UMaps Event Map Creator
//
// WHY IT IS STYLED INLINE. These pages do not share a stylesheet — some carry a
// `<style>` string with their own token palette, some are styled entirely with
// inline objects, and the tunnels/parking maps have neither. A class-based
// component would need a matching rule adding to each of them, which is exactly
// the kind of thing that gets added to four pages and forgotten on the fifth.
// Inline styles land the same everywhere.
//
// It takes its COLOUR from whatever it sits in (`color: inherit`) rather than
// naming one, so the same component reads correctly on the dark sign tool and
// on the white map bars. Only the tool half is dimmed, and by opacity rather
// than by a grey, so it stays legible on either.
//
// The mark is the circle-border PNG in public/ — the same file the printed sign
// badge uses, so the thing on the wall and the thing on the screen agree.

/** The circle mark. Percent-encoded: the filename has spaces, and a raw space
 *  in a src is the trap that has bitten this repo before (see signSheet.js). */
export const UMAPS_LOGO = '/UMaps%20Logo%20Circle%20Border.png';

/**
 * @param {string}  tool      what this page is ("Building Directories")
 * @param {number}  size      the mark's diameter in px; the type scales with it
 * @param {string?} href      where the lockup goes when clicked; null for none
 * @param {object}  style     merged onto the root, for per-page nudges
 */
export default function UMapsWordmark({
  tool = '', size = 22, href = '/umanitoba', style = {}, title,
}) {
  const root = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: Math.round(size * 0.36),
    color: 'inherit',
    textDecoration: 'none',
    // Sized off the mark so one number moves the whole lockup, and capped so a
    // big mark in a tall bar does not turn the name into a headline.
    fontSize: Math.min(17, Math.max(13, Math.round(size * 0.72))),
    lineHeight: 1.1,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    minWidth: 0,
    ...style,
  };

  const inner = (
    <>
      <img src={UMAPS_LOGO} alt="" width={size} height={size}
           style={{ width: size, height: size, display: 'block', flex: 'none', borderRadius: '50%' }} />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <b style={{ fontWeight: 800 }}>UMaps</b>
        {tool ? <span style={{ fontWeight: 600, opacity: 0.72 }}>{` ${tool}`}</span> : null}
      </span>
    </>
  );

  // A link when there is somewhere to go — the mark is the way back to the
  // pathfinder from a tool — and a plain span when there is not, rather than an
  // anchor with nothing behind it.
  return href
    ? <a href={href} style={root} title={title || `UMaps ${tool}`.trim()}>{inner}</a>
    : <span style={root} title={title || undefined}>{inner}</span>;
}
